import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatFullDateForUiLanguage } from "../../../shared/i18n/dateFormatting";
import { SUPPORTED_UI_LANGUAGES, TranslationKey, useI18n } from "../../../shared/i18n/i18n";
import { formatLanguageOptionLabel } from "../../../shared/i18n/languageCatalog";
import {
  confirmAdminTotpTwoFactor,
  disableAdminTwoFactor,
  enableAdminEmailTwoFactor,
  getAdminProfile,
  getAdminTwoFactorSettings,
  invalidateAdminSession,
  listAdminActiveSessions,
  listAdminTermsOfUseAcceptances,
  setupAdminTotpTwoFactor,
  updateAdminProfile,
} from "../../../shared/requests";
import {
  AdminActiveSession,
  AdminProfileDetail,
  AdminTermsOfUseAcceptanceItem,
  AdminTotpSetup,
  AdminTwoFactorSettings,
} from "../../types/adminEntities";
import { TermsOfUseModal } from "../auth/TermsOfUseModal";
import { AppPopup } from "../../../shared/components/AppPopup";
import { PlainQrCode } from "../common/ViewUrlQrCode";
import { useAdminLoading } from "../shell/AdminLoadingContext";
import { AdminDetailActions } from "../shell/detail/AdminDetailActions";
import { AdminDetailSectionLayout } from "../shell/detail/AdminDetailSectionLayout";
import { AdminSelectField } from "../shell/detail/AdminSelectField";
import { AdminTextField } from "../shell/detail/AdminTextField";

type AdminProfilePageProps = {
  t: (key: TranslationKey) => string;
};

type ProfileMode = "edit" | "view";
type ProfileTab = "basic" | "security" | "terms";
type TermsSortField = "acceptedAt" | "effectiveFrom";

export const AdminProfilePage = ({ t }: AdminProfilePageProps) => {
  const { language } = useI18n();
  const { track } = useAdminLoading();
  const location = useLocation();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminProfileDetail | null>(null);
  const [activeSessions, setActiveSessions] = useState<AdminActiveSession[]>([]);
  const [draftEmailLanguage, setDraftEmailLanguage] = useState("en");
  const [draftName, setDraftName] = useState("");
  const [historyItems, setHistoryItems] = useState<AdminTermsOfUseAcceptanceItem[]>([]);
  const [historyErrorMessage, setHistoryErrorMessage] = useState<null | string>(null);
  const [hasLoadedProfile, setHasLoadedProfile] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [mode, setMode] = useState<ProfileMode>("view");
  const [profileErrorMessage, setProfileErrorMessage] = useState<null | string>(null);
  const [popup, setPopup] = useState<"disable-2fa" | "discard" | "switch-2fa-email" | null>(null);
  const [pendingTab, setPendingTab] = useState<null | ProfileTab>(null);
  const [selectedTermsOfUseUuid, setSelectedTermsOfUseUuid] = useState<null | string>(null);
  const [sessionMessage, setSessionMessage] = useState<null | string>(null);
  const [securityErrorMessage, setSecurityErrorMessage] = useState<null | string>(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpSetup, setTotpSetup] = useState<AdminTotpSetup | null>(null);
  const [twoFactorSettings, setTwoFactorSettings] = useState<AdminTwoFactorSettings | null>(null);
  const [termsSortField, setTermsSortField] = useState<TermsSortField>("effectiveFrom");

  const activeTab: ProfileTab = location.pathname.endsWith("/terms")
    ? "terms"
    : location.pathname.endsWith("/security")
      ? "security"
      : "basic";

  const getProfileTabPath = useCallback((tab: ProfileTab) => (
    tab === "terms"
      ? "/a/profile/terms"
      : tab === "security"
        ? "/a/profile/security"
        : "/a/profile/basic"
  ), []);

  const sortedHistoryItems = useMemo(() => {
    const items = [...historyItems];
    items.sort((left, right) => {
      const leftTime = new Date(left[termsSortField]).getTime();
      const rightTime = new Date(right[termsSortField]).getTime();
      return rightTime - leftTime;
    });
    return items;
  }, [historyItems, termsSortField]);

  const emailLanguageOptions = useMemo(() => {
    return SUPPORTED_UI_LANGUAGES.map((tag) => ({
      label: formatLanguageOptionLabel(tag),
      value: tag,
    }));
  }, []);

  const loadProfile = useCallback(async () => {
    setProfileErrorMessage(null);
    setIsBusy(true);
    try {
      const { payload } = await track(async () => getAdminProfile());
      setDetail(payload);
      setDraftEmailLanguage(payload.emailLanguage);
      setDraftName(payload.name);
      setMode("view");
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setDetail(null);
      setProfileErrorMessage(message || fallbackMessage);
    } finally {
      setHasLoadedProfile(true);
      setIsBusy(false);
    }
  }, [t, track]);

  const loadHistory = useCallback(async () => {
    setHistoryErrorMessage(null);
    setIsHistoryLoading(true);
    try {
      const { payload } = await track(async () => listAdminTermsOfUseAcceptances());
      setHistoryItems(payload);
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setHistoryItems([]);
      setHistoryErrorMessage(message || fallbackMessage);
    } finally {
      setIsHistoryLoading(false);
    }
  }, [t, track]);

  const loadSecurity = useCallback(async () => {
    setSecurityErrorMessage(null);
    try {
      const [{ payload: settingsPayload }, { payload: sessionsPayload }] = await Promise.all([
        track(async () => getAdminTwoFactorSettings()),
        track(async () => listAdminActiveSessions()),
      ]);
      setTwoFactorSettings(settingsPayload);
      setActiveSessions(sessionsPayload);
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.loadFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  }, [t, track]);

  useEffect(() => {
    void Promise.all([loadProfile(), loadHistory(), loadSecurity()]);
  }, [loadHistory, loadProfile, loadSecurity]);

  const handleEdit = () => {
    if (!detail) {
      return;
    }
    setDraftEmailLanguage(detail.emailLanguage);
    setDraftName(detail.name);
    setMode("edit");
  };

  const handleTabChange = (nextTab: ProfileTab) => {
    if (nextTab === activeTab) {
      return;
    }
    if (mode === "edit" && activeTab === "basic") {
      setPendingTab(nextTab);
      setPopup("discard");
      return;
    }
    navigate(getProfileTabPath(nextTab));
  };

  const confirmDiscard = useCallback(() => {
    setPopup(null);
    if (!detail) {
      setPendingTab(null);
      return;
    }
    setDraftEmailLanguage(detail.emailLanguage);
    setDraftName(detail.name);
    setMode("view");
    if (pendingTab) {
      navigate(getProfileTabPath(pendingTab));
    }
    setPendingTab(null);
  }, [detail, getProfileTabPath, navigate, pendingTab]);

  const handleSave = async () => {
    if (!detail) {
      return;
    }
    setProfileErrorMessage(null);
    setIsBusy(true);
    try {
      await track(async () => updateAdminProfile({ emailLanguage: draftEmailLanguage, name: draftName }));
      setDetail({ ...detail, emailLanguage: draftEmailLanguage.trim(), name: draftName.trim() });
      setDraftEmailLanguage(draftEmailLanguage.trim());
      setDraftName(draftName.trim());
      setMode("view");
    } catch (error) {
      const fallbackMessage = t("admin.detail.error.saveFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setProfileErrorMessage(message || fallbackMessage);
    } finally {
      setIsBusy(false);
    }
  };

  const confirmEnableEmailTwoFactor = async () => {
    setPopup(null);
    setSecurityErrorMessage(null);
    try {
      await track(async () => enableAdminEmailTwoFactor());
      setTotpSetup(null);
      setTotpCode("");
      await loadSecurity();
    } catch (error) {
      const fallbackMessage = t("admin.twoFactor.error.enableFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  };

  const handleEnableEmailTwoFactor = () => {
    if (twoFactorSettings?.method === "TOTP") {
      setPopup("switch-2fa-email");
      return;
    }
    void confirmEnableEmailTwoFactor();
  };

  const handleStartTotpSetup = async () => {
    setSecurityErrorMessage(null);
    try {
      const { payload } = await track(async () => setupAdminTotpTwoFactor());
      setTotpSetup(payload);
      setTotpCode("");
    } catch (error) {
      const fallbackMessage = t("admin.twoFactor.error.setupFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  };

  const handleConfirmTotp = async () => {
    if (!totpSetup) {
      return;
    }
    setSecurityErrorMessage(null);
    try {
      const { response } = await track(async () => confirmAdminTotpTwoFactor({ code: totpCode }));
      if (!response.ok) {
        setSecurityErrorMessage(t("admin.twoFactor.error.invalidCode"));
        return;
      }
      setTotpSetup(null);
      setTotpCode("");
      await loadSecurity();
    } catch (error) {
      const fallbackMessage = t("admin.twoFactor.error.invalidCode");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  };

  const handleCloseTotpSetup = () => {
    setTotpSetup(null);
    setTotpCode("");
  };

  const handleDisableTwoFactor = () => {
    setPopup("disable-2fa");
  };

  const confirmDisableTwoFactor = async () => {
    setPopup(null);
    setSecurityErrorMessage(null);
    try {
      await track(async () => disableAdminTwoFactor());
      setTotpSetup(null);
      setTotpCode("");
      await loadSecurity();
    } catch (error) {
      const fallbackMessage = t("admin.twoFactor.error.disableFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  };

  const handleInvalidateSession = async (revocationHandle: string) => {
    setSecurityErrorMessage(null);
    setSessionMessage(null);
    try {
      await track(async () => invalidateAdminSession({ revocationHandle }));
      setSessionMessage(t("admin.sessions.invalidateSuccess"));
      await loadSecurity();
    } catch (error) {
      const fallbackMessage = t("admin.sessions.invalidateFailed");
      const message = error instanceof Error ? error.message : fallbackMessage;
      setSecurityErrorMessage(message || fallbackMessage);
    }
  };

  const isReadOnly = mode === "view" || isBusy;

  return (
    <section className="admin-shell-right-pane">
      <AdminDetailSectionLayout
        actions={null}
        errorMessage={profileErrorMessage}
        eyebrow={t("admin.route.profile.title")}
        title={t("admin.profile.title")}
      >
        <div className="admin-profile-tabs" role="tablist" aria-label={t("admin.profile.tabs.title")}>
          <button
            aria-controls="admin-profile-basic-panel"
            aria-selected={activeTab === "basic"}
            className={`admin-profile-tab${activeTab === "basic" ? " is-active" : ""}`}
            id="admin-profile-basic-tab"
            onClick={() => handleTabChange("basic")}
            role="tab"
            type="button"
          >
            {t("admin.profile.tab.basic")}
          </button>
          <button
            aria-controls="admin-profile-security-panel"
            aria-selected={activeTab === "security"}
            className={`admin-profile-tab${activeTab === "security" ? " is-active" : ""}`}
            id="admin-profile-security-tab"
            onClick={() => handleTabChange("security")}
            role="tab"
            type="button"
          >
            {t("admin.profile.tab.security")}
          </button>
          <button
            aria-controls="admin-profile-terms-panel"
            aria-selected={activeTab === "terms"}
            className={`admin-profile-tab${activeTab === "terms" ? " is-active" : ""}`}
            id="admin-profile-terms-tab"
            onClick={() => handleTabChange("terms")}
            role="tab"
            type="button"
          >
            {t("admin.profile.tab.terms")}
          </button>
        </div>

        <div
          aria-labelledby="admin-profile-security-tab"
          className={`admin-profile-panel${activeTab === "security" ? " is-active" : ""}`}
          id="admin-profile-security-panel"
          role="tabpanel"
        >
          <div className="admin-profile-security">
            {securityErrorMessage ? <div className="admin-detail-state is-error">{securityErrorMessage}</div> : null}
            <section className="admin-profile-security-section">
              <h4 className="admin-profile-security-section-title">{t("admin.twoFactor.sectionTitle")}</h4>
              <div className="admin-profile-radio-group" role="radiogroup" aria-label={t("admin.twoFactor.sectionTitle")}>
                <label className="admin-profile-radio-option">
                  <input
                    checked={!twoFactorSettings?.enabled}
                    name="two-factor-method"
                    onChange={() => {
                      if (twoFactorSettings?.enabled) {
                        handleDisableTwoFactor();
                      }
                    }}
                    type="radio"
                  />
                  <span>{t("admin.twoFactor.disable")}</span>
                </label>
                <label className="admin-profile-radio-option">
                  <input
                    checked={twoFactorSettings?.method === "EMAIL"}
                    name="two-factor-method"
                    onChange={handleEnableEmailTwoFactor}
                    type="radio"
                  />
                  <span>{t("admin.twoFactor.enableEmail")}</span>
                </label>
                <label
                  className="admin-profile-radio-option"
                  onMouseUp={() => {
                    if (twoFactorSettings?.method !== "TOTP") {
                      void handleStartTotpSetup();
                    }
                  }}
                >
                  <input
                    checked={twoFactorSettings?.method === "TOTP"}
                    name="two-factor-method"
                    onChange={() => undefined}
                    type="radio"
                  />
                  <span>{t("admin.twoFactor.setupAuthenticator")}</span>
                </label>
              </div>
            </section>
            <section className="admin-profile-security-section">
              <h4 className="admin-profile-security-section-title">{t("admin.sessions.sectionTitle")}</h4>
              {sessionMessage ? <p className="admin-detail-state">{sessionMessage}</p> : null}
              <p className="admin-profile-security-status">{t("admin.sessions.description")}</p>
              {activeSessions.length > 0 ? (
                <div className="admin-profile-terms-table-wrap">
                  <table className="admin-profile-terms-table">
                    <thead>
                      <tr>
                        <th scope="col">{t("admin.sessions.loginAt")}</th>
                        <th scope="col">{t("admin.sessions.ipAddress")}</th>
                        <th scope="col">{t("admin.sessions.userAgent")}</th>
                        <th scope="col">{t("admin.sessions.lastAccessedAt")}</th>
                        <th scope="col">{t("admin.sessions.expiresAt")}</th>
                        <th scope="col">{t("admin.profile.terms.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSessions.map((session) => (
                        <tr key={session.revocationHandle}>
                          <td data-label={t("admin.sessions.loginAt")}>{formatFullDateForUiLanguage(session.loginAt, language)}</td>
                          <td data-label={t("admin.sessions.ipAddress")}>{session.ipAddress}</td>
                          <td data-label={t("admin.sessions.userAgent")}>{session.userAgent}</td>
                          <td data-label={t("admin.sessions.lastAccessedAt")}>{formatFullDateForUiLanguage(session.lastAccessedAt, language)}</td>
                          <td data-label={t("admin.sessions.expiresAt")}>{formatFullDateForUiLanguage(session.expiresAt, language)}</td>
                          <td data-label={t("admin.profile.terms.actions")}>
                            {session.current ? (
                              <span className="admin-profile-session-current">{t("admin.sessions.current")}</span>
                            ) : (
                              <button className="admin-profile-terms-link" onClick={() => void handleInvalidateSession(session.revocationHandle)} type="button">
                                {t("admin.sessions.invalidate")}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="admin-detail-empty">{t("admin.sessions.empty")}</p>
              )}
            </section>
          </div>
        </div>

        <div
          aria-labelledby="admin-profile-basic-tab"
          className={`admin-profile-panel${activeTab === "basic" ? " is-active" : ""}`}
          id="admin-profile-basic-panel"
          role="tabpanel"
        >
          <div className="admin-profile-section-header">
            <div className="admin-detail-actions admin-profile-section-actions">
              <AdminDetailActions
                canCreate={false}
                hasDetail={detail !== null}
                isArchived={false}
                isBusy={isBusy}
                isDesktopNavHidden={false}
                isEditing={mode === "edit"}
                isMobileViewport
                showArchiveActions={false}
                showCreateAction={false}
                showNavToggle={false}
                onArchive={() => undefined}
                onCloseDesktopNav={() => undefined}
                onCreate={() => undefined}
                onDiscard={() => setPopup("discard")}
                onEdit={handleEdit}
                onOpenDesktopNav={() => undefined}
                onSave={() => void handleSave()}
                onUnarchive={() => undefined}
                t={t}
              />
            </div>
          </div>
          {detail ? (
            <form className="admin-detail-form">
              <AdminTextField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.name")}
                onChange={isReadOnly ? undefined : setDraftName}
                t={t}
                value={draftName}
                visibility="not-applicable"
              />
              <AdminSelectField
                isReadOnly={isReadOnly}
                label={t("admin.detail.field.emailLanguage")}
                onChange={isReadOnly ? () => undefined : setDraftEmailLanguage}
                options={emailLanguageOptions}
                t={t}
                value={draftEmailLanguage}
                visibility="not-applicable"
              />
              <AdminTextField
                isReadOnly
                label={t("admin.detail.field.email")}
                t={t}
                value={detail.email}
                visibility="not-applicable"
              />
              <AdminTextField
                isReadOnly
                label={t("admin.detail.field.role")}
                t={t}
                value={detail.superAdmin ? t("admin.profile.role.superAdmin") : t("admin.profile.role.user")}
                visibility="not-applicable"
              />
            </form>
          ) : !hasLoadedProfile ? (
            <p className="admin-detail-empty">{t("admin.navlist.loading")}</p>
          ) : (
            <p className="admin-detail-empty">{t("admin.detail.error.loadFailed")}</p>
          )}
        </div>

        <div
          aria-labelledby="admin-profile-terms-tab"
          className={`admin-profile-panel${activeTab === "terms" ? " is-active" : ""}`}
          id="admin-profile-terms-panel"
          role="tabpanel"
        >
          {historyErrorMessage ? <div className="admin-detail-state is-error">{historyErrorMessage}</div> : null}
          {isHistoryLoading ? <p className="admin-detail-empty">{t("admin.navlist.loading")}</p> : null}
          {!isHistoryLoading && !historyErrorMessage && historyItems.length === 0 ? (
            <p className="admin-detail-empty">{t("admin.profile.terms.empty")}</p>
          ) : null}
          {!isHistoryLoading && !historyErrorMessage && historyItems.length > 0 ? (
            <div className="admin-profile-terms-table-wrap">
              <table className="admin-profile-terms-table">
                <thead>
                  <tr>
                    <th scope="col">
                      <button
                        className={`admin-profile-terms-sort${termsSortField === "effectiveFrom" ? " is-active" : ""}`}
                        onClick={() => setTermsSortField("effectiveFrom")}
                        type="button"
                      >
                        {t("admin.profile.terms.effectiveFrom")}
                      </button>
                    </th>
                    <th scope="col">
                      <button
                        className={`admin-profile-terms-sort${termsSortField === "acceptedAt" ? " is-active" : ""}`}
                        onClick={() => setTermsSortField("acceptedAt")}
                        type="button"
                      >
                        {t("admin.profile.terms.acceptedAt")}
                      </button>
                    </th>
                    <th scope="col">{t("admin.profile.terms.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedHistoryItems.map((item) => (
                    <tr key={`${item.termsOfUseUuid}:${item.acceptedAt}`}>
                      <td data-label={t("admin.profile.terms.effectiveFrom")}>
                        {formatFullDateForUiLanguage(item.effectiveFrom, language)}
                      </td>
                      <td data-label={t("admin.profile.terms.acceptedAt")}>
                        {formatFullDateForUiLanguage(item.acceptedAt, language)}
                      </td>
                      <td data-label={t("admin.profile.terms.actions")}>
                        <button
                          className="admin-profile-terms-link"
                          onClick={() => setSelectedTermsOfUseUuid(item.termsOfUseUuid)}
                          type="button"
                        >
                          {t("admin.profile.terms.open")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </AdminDetailSectionLayout>

      <AppPopup
        actions={(
          <>
            <button className="admin-popup-action" onClick={() => setPopup(null)} type="button">
              {t("admin.popup.cancel")}
            </button>
            <button className="admin-popup-action primary" onClick={confirmDiscard} type="button">
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "discard"}
        onClose={() => {
          setPopup(null);
          setPendingTab(null);
        }}
        title={t("admin.detail.confirm.discardTitle")}
      >
        <p>{t("admin.detail.confirm.discard")}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button className="admin-popup-action" onClick={() => setPopup(null)} type="button">
              {t("admin.popup.cancel")}
            </button>
            <button className="admin-popup-action primary" onClick={() => void confirmDisableTwoFactor()} type="button">
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "disable-2fa"}
        onClose={() => setPopup(null)}
        title={t("admin.twoFactor.disableConfirmTitle")}
      >
        <p>{t("admin.twoFactor.disableConfirmBody.account")}</p>
      </AppPopup>
      <AppPopup
        actions={(
          <>
            <button className="admin-popup-action" onClick={() => setPopup(null)} type="button">
              {t("admin.popup.cancel")}
            </button>
            <button className="admin-popup-action primary" onClick={() => void confirmEnableEmailTwoFactor()} type="button">
              {t("admin.popup.confirm")}
            </button>
          </>
        )}
        isOpen={popup === "switch-2fa-email"}
        onClose={() => setPopup(null)}
        title={t("admin.twoFactor.switchToEmailConfirmTitle")}
      >
        <p>{t("admin.twoFactor.switchToEmailConfirmBody")}</p>
      </AppPopup>
      <TermsOfUseModal
        isOpen={selectedTermsOfUseUuid !== null}
        onClose={() => setSelectedTermsOfUseUuid(null)}
        t={t}
        termsOfUseUuid={selectedTermsOfUseUuid ?? undefined}
      />
      <AppPopup
        actions={(
          <>
            <button className="admin-popup-action" onClick={handleCloseTotpSetup} type="button">
              {t("admin.popup.cancel")}
            </button>
            <button className="admin-popup-action primary" onClick={() => void handleConfirmTotp()} type="button">
              {t("admin.twoFactor.confirmAuthenticator")}
            </button>
          </>
        )}
        isOpen={totpSetup !== null}
        onClose={handleCloseTotpSetup}
        panelClassName="admin-terms-modal-panel"
        title={t("admin.twoFactor.setupAuthenticator")}
      >
        {totpSetup ? (
          <div className="admin-profile-totp-setup">
            {securityErrorMessage ? <div className="admin-detail-state is-error">{securityErrorMessage}</div> : null}
            <p className="admin-profile-security-status">{t("admin.twoFactor.setupInstructions")}</p>
            <div className="admin-profile-totp-qr">
              <PlainQrCode value={totpSetup.otpauthUrl} />
            </div>
            <dl className="admin-profile-totp-manual">
              <div>
                <dt>{t("admin.twoFactor.codeName")}</dt>
                <dd>{`${totpSetup.issuer}:${totpSetup.organizationName}`}</dd>
              </div>
              <div>
                <dt>{t("admin.twoFactor.yourKey")}</dt>
                <dd>{totpSetup.secret}</dd>
              </div>
              <div>
                <dt>{t("admin.twoFactor.type")}</dt>
                <dd>{t("admin.twoFactor.timeBased")}</dd>
              </div>
            </dl>
            <label className="admin-detail-field">
              <span className="admin-detail-label">{t("admin.twoFactor.confirmCode")}</span>
              <input
                className="admin-detail-input"
                inputMode="numeric"
                onChange={(event) => setTotpCode(event.target.value)}
                value={totpCode}
              />
            </label>
          </div>
        ) : null}
      </AppPopup>
    </section>
  );
};
